import React, { useEffect, useState } from 'react';
import { ProjectAndCustomer } from '../project/projectType';
import { Customer } from '../customer/customerType';
import ProjectApi from '../project/projectApi';
import { useNavigate, useLocation } from 'react-router-dom';
import HorizontalMenu from '../components/common/horizontalMenu';
import { X, DollarSign, Edit, FilePlus, ChevronUp, ChevronDown, Image as ImageIcon, Wrench } from 'lucide-react';
import { formatDate, formatTimestamp } from '../lib/global_function';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Button } from '../components/ui/button';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Link } from '../components/ui/link';


const ProjectPage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Project', path: '/main/project' },
  ];

  const [projectsAndCustomers, setProjectsAndCustomers] = useState<ProjectAndCustomer[]>([]);
  const [selectedProject, setSelectedProject] = useState<ProjectAndCustomer | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>(null);
  const [hoveredAddress, setHoveredAddress] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const customer = location.state?.customer as Customer | undefined;

  useEffect(() => {
    const fetchProjectsAndCustomers = async () => {
      try {
        let fetchedProjectsAndCustomers: ProjectAndCustomer[];
        if (customer?.customer_id) {
          fetchedProjectsAndCustomers = await ProjectApi.getProjectsByCustomer(customer.customer_id);
        } else {
          fetchedProjectsAndCustomers = await ProjectApi.getProjectsAndCustomers();
        }
        setProjectsAndCustomers(fetchedProjectsAndCustomers);

      } catch (error) {
        if (error instanceof Error) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage('An unknown error occurred.');
        }
      }
    };
    fetchProjectsAndCustomers();
  }, [customer]);

  const handleClearSelection = () => setSelectedProject(null);

  const handleCreateEstimate = () => {
    if (selectedProject) navigate('/main/project/estimate/create', { state: { projectAndCustomer: selectedProject } });
  };

  const handleCreateInvoice = () => {
    if (selectedProject) navigate('/main/project/invoice/create', { state: { projectAndCustomer: selectedProject } });
  };

  const handleTrackPayment = () => {
    if (selectedProject) navigate('/main/customerpayment', { state: { projectAndCustomer: selectedProject } });
  };

  const handleDeleteProject = () => {
    if (selectedProject) navigate('/main/project/delete', { state: { selectedProject } });
  };

  const handleAddPhoto = () => {
    if (selectedProject) navigate('/main/project/photo', { state: { selectedProject } });
  };

  const handleSortByName = (order: 'asc' | 'desc') => {
    setSortOrder(order);
    const sortedProjects = [...projectsAndCustomers].sort((a, b) =>
      order === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)
    );
    setProjectsAndCustomers(sortedProjects);
  };

  const handleSortByCreateDate = (order: 'asc' | 'desc') => {
    setSortOrder(order);
    const sortedProjects = [...projectsAndCustomers].sort((a, b) =>
      order === 'asc' ? a.created_at.localeCompare(b.created_at) : b.created_at.localeCompare(a.created_at)
    );
    setProjectsAndCustomers(sortedProjects);
  };

  const handleCreateProject = () => navigate('/main/project/create');

  const handleEditProject = (projectAndCustomer: ProjectAndCustomer) => {
    navigate('/main/project/update', { state: { projectAndCustomer } });
  };

  const handleMouseEnter = (address: string) => {
    setHoveredAddress(address);
  };

  const handleMouseLeave = () => {
    setHoveredAddress(null);
  };

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />

        <div className="mt-6">
          <div className="mb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
              <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
                <Wrench className="size-7 text-primary" />
                Project List
              </h1>
              <Button size="medium" variant="default" onClick={handleCreateProject}>
                <FilePlus size={18} />
                Create Project
              </Button>
            </div>


            <p className="text-sm text-paragraph">
              Select radio button in front of Name to edit a project, create estimate/invoice or add photo to project.
            </p>
          </div>

          {/* Error Message */}
          {errorMessage && (
            <Alert variant="error">
              {errorMessage}
            </Alert>
          )}

          {/* Action Bar - Mobile Responsive */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg flex flex-wrap items-center gap-3 text-sm">
            {/* Selection Status */}
            <div className="flex items-center gap-2">
              {selectedProject ? (
                <>
                  <button
                    onClick={handleClearSelection}
                    className="text-gray-600 hover:text-gray-900"
                    title="Clear Selection"
                  >
                    <X className="size-5" />
                  </button>
                  <Badge variant="default">1 project selected</Badge>
                </>
              ) : (
                <span className="text-gray-500 text-sm">No project selected</span>
              )}
            </div>

            <span className="text-gray-300">|</span>
            
            {/* Action Buttons - Wrapped for Mobile */}
            <div className="flex flex-wrap gap-2 text-sm">
              <button
                onClick={() => selectedProject && handleEditProject(selectedProject)}
                disabled={!selectedProject}
                className={`flex items-center gap-1.5 ${selectedProject
                  ? 'text-primary hover:text-primary-hover cursor-pointer'
                  : 'text-gray-400 cursor-not-allowed'
                  }`}
                title={selectedProject ? "Edit Project" : "Select a project to edit"}
              >
                <Edit className="size-4" /> Edit
              </button>

              <button
                onClick={handleCreateEstimate}
                disabled={!selectedProject}
                className={`flex items-center gap-1.5 ${selectedProject
                  ? 'text-primary hover:text-primary-hover cursor-pointer'
                  : 'text-gray-400 cursor-not-allowed'
                  }`}
                title={selectedProject ? "Create Estimate" : "Select a project to create estimate"}
              >
                <FilePlus className="size-4" /> Create Estimate
              </button>

              <button
                onClick={handleCreateInvoice}
                disabled={!selectedProject}
                className={`flex items-center gap-1.5 ${selectedProject
                  ? 'text-primary hover:text-primary-hover cursor-pointer'
                  : 'text-gray-400 cursor-not-allowed'
                  }`}
                title={selectedProject ? "Create Invoice" : "Select a project to create invoice"}
              >
                <FilePlus className="size-4" /> Create Invoice
              </button>

              <button
                onClick={handleAddPhoto}
                disabled={!selectedProject}
                className={`flex items-center gap-1.5 ${selectedProject
                  ? 'text-primary hover:text-primary-hover cursor-pointer'
                  : 'text-gray-400 cursor-not-allowed'
                  }`}
                title={selectedProject ? "Add Photo" : "Select a project to add photo"}
              >
                <ImageIcon className="size-4" /> Add Photo
              </button>
            </div>
          </div>

          {/* Project Table - Desktop Only */}
          {projectsAndCustomers.length > 0 ? (
            <>
              <div className="hidden md:block overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="w-12 px-4 py-3"></th>
                      <th className="px-4 py-3 text-left">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-foreground">Name</span>
                          <div className="flex flex-col">
                            <button
                              onClick={() => handleSortByName('asc')}
                              className="text-gray-500 hover:text-gray-700"
                              title="Sort Ascending"
                            >
                              <ChevronUp className="size-3" />
                            </button>
                            <button
                              onClick={() => handleSortByName('desc')}
                              className="text-gray-500 hover:text-gray-700"
                              title="Sort Descending"
                            >
                              <ChevronDown className="size-3" />
                            </button>
                          </div>
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-foreground">Description</th>
                      <th className="px-4 py-3 text-left font-semibold text-foreground">Type</th>
                      <th className="px-4 py-3 text-left font-semibold text-foreground">Start-End Date</th>
                      <th className="px-4 py-3 text-left font-semibold text-foreground">Customer</th>
                      <th className="px-4 py-3 text-left">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-foreground">Created On</span>
                          <div className="flex flex-col">
                            <button
                              onClick={() => handleSortByCreateDate('asc')}
                              className="text-gray-500 hover:text-gray-700"
                              title="Sort Ascending"
                            >
                              <ChevronUp className="size-3" />
                            </button>
                            <button
                              onClick={() => handleSortByCreateDate('desc')}
                              className="text-gray-500 hover:text-gray-700"
                              title="Sort Descending"
                            >
                              <ChevronDown className="size-3" />
                            </button>
                          </div>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {projectsAndCustomers.map((projectAndCustomer) => {
                      const address = [
                        projectAndCustomer.customer_address_1?.trim(),
                        projectAndCustomer.customer_city?.trim(),
                        projectAndCustomer.customer_state?.trim(),
                        projectAndCustomer.customer_zip?.trim(),
                      ].filter(Boolean).join(', ');

                      const truncatedDescription =
                        projectAndCustomer.project_description && projectAndCustomer.project_description.length > 200
                          ? `${projectAndCustomer.project_description.slice(0, 200)}...`
                          : projectAndCustomer.project_description || 'N/A';

                      return (
                        <tr key={projectAndCustomer.project_id}>
                          <td className="px-4 py-3 text-center">
                            <input
                              type="radio"
                              name="projectSelectDescktop"
                              onChange={() => setSelectedProject(projectAndCustomer)}
                              checked={selectedProject?.project_id === projectAndCustomer.project_id}
                              className="text-left size-4 text-primary cursor-pointer"
                                style={{ accentColor: 'rgb(248, 98, 3)' }}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => handleEditProject(projectAndCustomer)}
                              className="w-full text-left text-foreground hover:text-primary underline font-medium"
                            >
                              {projectAndCustomer.name}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-paragraph">{truncatedDescription}</td>
                          <td className="px-4 py-3 text-paragraph">{projectAndCustomer.project_type}</td>
                          <td className="px-4 py-3 text-paragraph">
                            {projectAndCustomer?.start_date ? formatDate(projectAndCustomer.start_date) : 'N/A'} -{' '}
                            {projectAndCustomer?.end_date ? formatDate(projectAndCustomer.end_date) : 'N/A'}
                          </td>
                          <td
                            className="px-4 py-3 relative cursor-pointer text-foreground hover:text-primary"
                            onMouseEnter={() => setHoveredAddress(address)}
                            onMouseLeave={() => setHoveredAddress(null)}
                          >
                            <div
                              className="relative inline-block"
                              onMouseEnter={() => handleMouseEnter(address)}
                              onMouseLeave={handleMouseLeave}
                            >
                              <span className="underline cursor-pointer">
                                {projectAndCustomer.customer_first_name} {projectAndCustomer.customer_last_name}
                              </span>

                              {hoveredAddress === address && address && (
                                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-3 z-10 min-w-[200px]">
                                  {hoveredAddress}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-paragraph">{formatTimestamp(projectAndCustomer.created_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Project Cards - Mobile Only */}
              <div className="md:hidden space-y-3">
                {projectsAndCustomers.map((projectAndCustomer) => {
                  const address = [
                    projectAndCustomer.customer_address_1?.trim(),
                    projectAndCustomer.customer_city?.trim(),
                    projectAndCustomer.customer_state?.trim(),
                    projectAndCustomer.customer_zip?.trim(),
                  ].filter(Boolean).join(', ');

                  return (
                    <div
                      key={projectAndCustomer.project_id}
                      className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
                    >
                      <div className="flex items-start gap-3 mb-3">
                        <input
                          type="radio"
                          name="projectSelectMobile"
                          onChange={() => setSelectedProject(projectAndCustomer)}
                          checked={selectedProject?.project_id === projectAndCustomer.project_id}
                          className="size-4 text-primary cursor-pointer mt-1"
                          style={{ accentColor: 'rgb(248, 98, 3)' }}
                        />
                        <div className="flex-1">
                          <button
                            onClick={() => handleEditProject(projectAndCustomer)}
                            className="text-foreground hover:text-primary underline font-semibold text-lg"
                          >
                            {projectAndCustomer.name}
                          </button>
                        </div>
                      </div>

                      <div className="space-y-2 text-sm ml-7">
                        {projectAndCustomer.project_description && (
                          <div>
                            <span className="text-gray-600">Description: </span>
                            <span className="text-paragraph">
                              {projectAndCustomer.project_description.length > 100
                                ? `${projectAndCustomer.project_description.slice(0, 100)}...`
                                : projectAndCustomer.project_description}
                            </span>
                          </div>
                        )}
                        <div>
                          <span className="text-gray-600">Type: </span>
                          <span className="text-paragraph">{projectAndCustomer.project_type || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Dates: </span>
                          <span className="text-paragraph">
                            {projectAndCustomer?.start_date ? formatDate(projectAndCustomer.start_date) : 'N/A'} -{' '}
                            {projectAndCustomer?.end_date ? formatDate(projectAndCustomer.end_date) : 'N/A'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Customer: </span>
                          <span className="text-paragraph">
                            {projectAndCustomer.customer_first_name} {projectAndCustomer.customer_last_name}
                          </span>
                        </div>
                        {address && (
                          <div>
                            <span className="text-gray-600">Address: </span>
                            <span className="text-paragraph">{address}</span>
                          </div>
                        )}
                        <div>
                          <span className="text-gray-600">Created: </span>
                          <span className="text-paragraph">{formatTimestamp(projectAndCustomer.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="text-center py-12 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-paragraph mb-6">No projects found, please create one.</p>
              <Button onClick={handleCreateProject} size="medium" variant="default">
                <FilePlus className="size-5" />
                Create Project
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProjectPage;